
export const asciiWhitespace = '\u0009\u000a\u000c\u000d\u0020';
export const htmlNamespace = 'http://www.w3.org/1999/xhtml';
export const xmlNamespace = 'http://www.w3.org/XML/1998/namespace';
export const fragmentDirectiveDelimiter = ':~:';
export const voidElements = ['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr'];


/**
 * Checks if an element serializes as a void element.
 *
 * @param {Element} element - The DOM element to check.
 * @returns {boolean} - Returns true if the element serializes as a void element, otherwise false.
 */
export function serializesAsVoid(element) {
    const voidElementsSet = new Set(voidElements.concat(['basefont', 'bgsound', 'frame', 'keygen']));
    return element.namespaceURI === htmlNamespace && voidElementsSet.has(element.localName);
}


/**
 * Checks if the given element is currently being rendered.
 *
 * This function determines if an element is being rendered by checking if it does not have the 'hidden' attribute.
 *
 * @param {HTMLElement} element - The DOM element to check.
 * @returns {boolean} - Returns true if the element is being rendered, otherwise false.
 */
export function isBeingRendered(element) {
    return !element.hasAttribute('hidden');
}


export function isDescendant(nodeA, nodeB) {
    if (nodeA.parentNode === nodeB)
        return true;
    const nodeC = nodeA.parentNode;
    if (nodeC && isDescendant(nodeC, nodeB))
        return true;
    return false;
}

/**
 * Calculates the length of a given DOM node.
 *
 * @param {Node} node - The DOM node whose length is to be calculated.
 * @returns {number} - The length of the node. For document type nodes, it returns 0.
 *                      For text, processing instruction, and comment nodes, it returns the length of the data.
 *                      For other node types, it returns the number of child nodes.
 */
export function nodeLength(node) {
    switch (node.nodeType) {
        case Node.DOCUMENT_TYPE_NODE:
            return 0;
        case Node.TEXT_NODE:
        case Node.PROCESSING_INSTRUCTION_NODE:
        case Node.COMMENT_NODE:
            return node.data.length;
        default:
            return node.childNodes.length;
    }
}


/**
 * Returns the next node in the shadow-including tree order.
 * If the given node is a shadow host, it recursively calls itself with the shadow root of the node.
 * Otherwise, it returns the next node in the regular tree order.
 *
 * @param {Node} node - The node from which to find the next node in the shadow-including tree order.
 * @returns {Node} - The next node in the shadow-including tree order.
 */
export function nextNodeInShadowIncludingTreeOrder(node) {
    if (isShadowHost(node)) {
        return nextNodeInShadowIncludingTreeOrder(node.shadowRoot);
    } else {
        return nextNode(node);
    }
}


/**
 * Checks if a given node is a shadow host.
 * A shadow host is an element that has a shadow root.
 *
 * @param {Node} node - The node to check.
 * @returns {boolean} True if the node is a shadow host, otherwise false.
 */
export function isShadowHost(node) {
    return (isElement(node) && node.shadowRoot !== null);
}

/**
 * Checks if nodeA is a shadow-including descendant of nodeB.
 * A shadow-including descendant is a node that is either a descendant of another node
 * or is part of the shadow DOM of a node that is a descendant.
 *
 * @param {Node} nodeA - The node to check if it is a shadow-including descendant.
 * @param {Node} nodeB - The node to check against.
 * @returns {boolean} - Returns true if nodeA is a shadow-including descendant of nodeB, otherwise false.
 */
export function isShadowIncludingDescendant(nodeA, nodeB) {
    if (isDescendant(nodeA, nodeB))
        return true;
    const nodeARoot = nodeA.getRootNode();
    if (nodeARoot instanceof ShadowRoot && isShadowIncludingInclusiveDescendant(nodeARoot.host, nodeB))
        return true;
    return false;
}

/**
 * Checks if nodeA is a shadow-including inclusive descendant of nodeB.
 * 
 * A node is a shadow-including inclusive descendant of another node if it is a descendant of that node
 * or if it is a descendant of a shadow tree whose shadow-including root is a descendant of that node.
 * 
 * @param {Node} nodeA - The node to check if it is a shadow-including inclusive descendant.
 * @param {Node} nodeB - The node to check against.
 * @returns {boolean} - Returns true if nodeA is a shadow-including inclusive descendant of nodeB, otherwise false.
 */
export function isShadowIncludingInclusiveDescendant(nodeA, nodeB) {
    if (nodeA === nodeB)
        return true;
    if (isShadowIncludingDescendant(nodeA, nodeB))
        return true;
    return false;
}

export function isShadowIncludingAncestor(nodeA, nodeB) {
    return isShadowIncludingDescendant(nodeB, nodeA);
}

export function isShadowIncludingInclusiveAncestor(nodeA, nodeB) {
    if (nodeA === nodeB)
        return true;
    if (isShadowIncludingAncestor(nodeA, nodeB))
        return true;
    return false;
}

export function substringData(node, offset, count) {
    const length = nodeLength(node);
    if (offset > length)
        throw new DOMException('', 'IndexSizeError');
    if (offset + count > length) {
        return node.data.substring(offset);
    }
    return node.data.substring(offset, offset + count);
}

export function nextNode(node) {
    const walker = (node.ownerDocument ?? node).createTreeWalker(node.getRootNode());
    walker.currentNode = node;
    return walker.nextNode();
}

export function isElement(node) {
    return node.nodeType === Node.ELEMENT_NODE;
}

export function processAndConsumeFragmentDirective(url) {
    // “Each document has an associated fragment directive which is either null or an ASCII string holding data used by the UA to process the resource. It is initially null.”
    let documentFragmentDirective = null;

    // 1. “Let raw fragment be equal to url’s fragment.”
    // (as we only have access to the serialised URL, we extract the fragment again)
    const rawFragment = url.split('#')[1] ?? null;

    // 2. “If raw fragment is non-null and contains the fragment directive delimiter as a substring:”
    if (rawFragment !== null && rawFragment.includes(fragmentDirectiveDelimiter)) {
        // 1. “Let fragmentDirectivePosition be the index of the first instance of the fragment directive delimiter in raw fragment.”
        let fragmentDirectivePosition = rawFragment.indexOf(fragmentDirectiveDelimiter);

        // 2. “Let fragment be the substring of raw fragment starting at 0 of count fragmentDirectivePosition.”
        const fragment = rawFragment.substring(0, fragmentDirectivePosition);

        // 3. “Advance fragmentDirectivePosition by the length of fragment directive delimiter.”
        fragmentDirectivePosition += fragmentDirectiveDelimiter.length;

        // 4. “Let fragment directive be the substring of raw fragment starting at fragmentDirectivePosition.”
        const fragmentDirective = rawFragment.substring(fragmentDirectivePosition);

        // 5. “Set url’s fragment to fragment.”
        // (as we only have access to the serialised URL, we manually replace its fragment part)
        url = url.split('#')[0] + (fragment !== null ? '#' + fragment : '');

        // 6. “Set document’s fragment directive to fragment directive.”
        documentFragmentDirective = fragmentDirective;
    }

    // For testing/trying purposes, we return what should now be the document’s URL and fragment directive.
    return { url, documentFragmentDirective };
}

// https://wicg.github.io/scroll-to-text-fragment/#parse-a-text-directive
// “To parse a text directive, on an ASCII string text directive input, run these steps:”
export function parseTextDirective(textDirectiveInput) {
    // 1. “Assert: text directive input matches the production TextDirective.”
    // assert(isTextFragmentDirective(textDirectiveInput));

    // 2. “Let textDirectiveString be the substring of text directive input starting at index 5.”
    const textDirectiveString = textDirectiveInput.substring(5);

    // 3. “Let tokens be a list of strings that is the result of splitting textDirectiveString on commas.”
    const tokens = textDirectiveString.split(',');

    // 4. “If tokens has size less than 1 or greater than 4, return null.”
    if (tokens.length < 1 || tokens.length > 4)
        return null;

    // 5. “If any of tokens’s items are the empty string, return null.”
    if (tokens.some(token => token === ''))
        return null;

    // 6. “Let retVal be a ParsedTextDirective with each of its items initialized to null.”
    const retVal = {
        textStart: null,
        textEnd: null,
        prefix: null,
        suffix: null,
    };

    // 7. “Let potential prefix be the first item of tokens.”
    const potentialPrefix = tokens[0];

    // 8. “If the last character of potential prefix is U+002D (-), then:”
    if (potentialPrefix.endsWith('-')) {
        // 1. “Set retVal’s prefix to the result of removing the last character from potential prefix.
        retVal.prefix = decodeURIComponent(potentialPrefix.substring(0, potentialPrefix.length - 1));
        // 2. “Remove the first item of the list tokens.”
        tokens.shift();
    }

    // 9. “Let potential suffix be the last item of tokens, if one exists, null otherwise.”
    const potentialSuffix = tokens[tokens.length - 1] ?? null;

    // 10. “If potential suffix is non-null and its first character is U+002D (-), then:”
    if (potentialSuffix !== null && potentialSuffix.startsWith('-')) {
        // 1. “Set retVal’s suffix to the result of removing the first character from potential suffix.”
        retVal.suffix = decodeURIComponent(potentialSuffix.substring(1));
        // 2. “Remove the last item of the list tokens.”
        tokens.pop();
    }

    // 11. “If tokens has size not equal to 1 nor 2 then return null.”
    if (tokens.length !== 1 && tokens.length !== 2)
        return null;

    // 12. “Set retVal’s textStart be the first item of tokens.”
    retVal.textStart = decodeURIComponent(tokens[0]);

    // 13. “If tokens has size 2, then set retVal’s textEnd be the last item of tokens.”
    if (tokens.length === 2)
        retVal.textEnd = decodeURIComponent(tokens[tokens.length - 1]);

    // 14. “Return retVal.”
    return retVal;
}

// https://wicg.github.io/scroll-to-text-fragment/#valid-fragment-directive
// “A valid fragment directive is a sequence of characters that appears in the fragment directive that matches the production:”
export function isValidFragmentDirective(input) {
    // TODO (use PEG.js?)
    return true; // TEMP
}

export function isTextFragmentDirective(input) {
    // TODO (use PEG.js?)
    return input.startsWith('text='); // TEMP
}

export function scrollToTheFragment(indicatedPart) {
    const [target, range] = indicatedPart;
    if (range !== null) {
        scrollRangeIntoView(range, 'auto', 'center', 'nearest', target);
    } else {
        scrollElementIntoView(target, 'auto', 'start', 'nearest');
    }
}


export function indicatedPartOfTheDocument_beginning({ document, documentFragmentDirective, documentAllowTextFragmentDirective }) {
    let documentIndicatedPart = undefined;

    // 1. “Let fragment directive string be the document’s fragment directive.”
    const fragmentDirectiveString = documentFragmentDirective;

    // 2. “If the document’s allowTextFragmentDirective flag is true then:”
    if (documentAllowTextFragmentDirective === true) {

        // 1. “Let ranges be a list that is the result of running the process a fragment directive steps with fragment directive string and the document.”
        let ranges = processFragmentDirective(fragmentDirectiveString, document);

        // 2. “If ranges is non-empty, then:”
        if (ranges.length > 0) {

            // 1. “Let range be the first item of ranges.”
            const range = ranges[0];

            // 2. “Let node be the first common ancestor of range’s start node and start node.”
            // XXX This looks silly. Was “start node and end node” meant here?
            let node = firstCommonAncestor(range.startContainer, range.startContainer);

            // 3. “While node is not an element, set node to node’s parent.”
            // XXX Could loop forever! Or is it guaranteed that node has an element as ancestor? This may be a valid but fragile assumption.
            while (!isElement(node))
                node = node.parentNode;

            // 4. “The indicated part of the document is node and range; return.”
            documentIndicatedPart = [node, range];

            // return;
            return { documentIndicatedPart, ranges }; // To allow testing it out.
        }
    }

    return { documentIndicatedPart };
}

export function firstCommonAncestor(nodeA, nodeB) {
    // 1. “Let commonAncestor be nodeA.”
    let commonAncestor = nodeA;

    // 2. “While commonAncestor is non-null and is not a shadow-including inclusive ancestor of nodeB, let commonAncestor be commonAncestor’s shadow-including parent.”
    while (commonAncestor && !isShadowIncludingInclusiveAncestor(commonAncestor, nodeB)) {
        commonAncestor = shadowIncludingParent(commonAncestor);
    }

    // 3. “Return commonAncestor.”
    return commonAncestor;
}

export function shadowIncludingParent(node) {
    // 1. “If node is a shadow root, return node’s host.”
    if (node instanceof ShadowRoot)
        return node.host;

    // 2. “Otherwise, return node’s parent.”
    return node.parentNode;
}
// https://wicg.github.io/scroll-to-text-fragment/#scroll-a-domrect-into-view
// “Move the scroll an element into view algorithm’s steps 3-14 into a new algorithm scroll a DOMRect into view, with input DOMRect bounding box, ScrollIntoViewOptions dictionary options, and element startingElement.”
// “Also move the recursive behavior described at the top of the scroll an element into view algorithm to the scroll a DOMRect into view algorithm: "run these steps for each ancestor element or viewport of startingElement that establishes a scrolling box scrolling box, in order of innermost to outermost scrolling box".”
// “To scroll a DOMRect into view given a DOMRect bounding box, a scroll behavior behavior, a block flow direction position block, and an inline base direction position inline, and element startingElement, means to run these steps for each ancestor element or viewport of startingElement that establishes a scrolling box scrolling box, in order of innermost to outermost scrolling box:”
export function scrollDomRectIntoView(boundingBox, behavior, block, inline, startingElement) {
    // “OMITTED”
    // TODO Create/borrow a complete implementation.
    // TEMP assume the window is the only scrolling box, block=vertical and inline=horizontal, …
    function applyScrollLogicalPosition({
        position,
        boundingBoxRelativeEdgeBegin,
        boundingBoxRelativeEdgeEnd,
        boundingBoxSize,
        scrollBoxAbsoluteEdgeBegin,
        scrollBoxSize,
    }) {
        const boundingBoxAbsoluteEdgeBegin = scrollBoxAbsoluteEdgeBegin + boundingBoxRelativeEdgeBegin;
        const boundingBoxAbsoluteEdgeEnd = boundingBoxAbsoluteEdgeBegin + boundingBoxSize;
        boundingBoxRelativeEdgeEnd -= scrollBoxSize; // measure relative to scroll box’s end, not start.
        switch (position) {
            case 'start':
                return boundingBoxAbsoluteEdgeBegin;
            case 'end':
                return boundingBoxAbsoluteEdgeEnd - scrollBoxSize;
            case 'center':
                return boundingBoxAbsoluteEdgeBegin + boundingBoxSize / 2 - scrollBoxSize / 2;
            case 'nearest':
                const fitsInView = boundingBoxSize < scrollBoxSize; // XXX CSSWG spec seems to forget the case in which the sizes are equal. Here we interpret “greater than” as “greater than or equal to”.
                if (boundingBoxRelativeEdgeBegin < 0 && boundingBoxRelativeEdgeEnd > 0)
                    return undefined;
                else if (boundingBoxRelativeEdgeBegin < 0 && fitsInView || boundingBoxRelativeEdgeEnd > 0 && !fitsInView)
                    return boundingBoxAbsoluteEdgeBegin;
                else if (boundingBoxRelativeEdgeBegin < 0 && !fitsInView || boundingBoxRelativeEdgeEnd > 0 && fitsInView)
                    return boundingBoxAbsoluteEdgeEnd - scrollBoxSize;
        }
        return undefined;
    }
    const top = applyScrollLogicalPosition({
        position: block ?? 'start', // presuming same default as for Element.scrollIntoView
        boundingBoxRelativeEdgeBegin: boundingBox.top,
        boundingBoxRelativeEdgeEnd: boundingBox.bottom,
        scrollBoxAbsoluteEdgeBegin: window.scrollY,
        boundingBoxSize: boundingBox.height,
        scrollBoxSize: document.documentElement.clientHeight,
    });
    const left = applyScrollLogicalPosition({
        position: inline ?? 'nearest', // presuming same default as for Element.scrollIntoView
        boundingBoxRelativeEdgeBegin: boundingBox.left,
        boundingBoxRelativeEdgeEnd: boundingBox.right,
        boundingBoxSize: boundingBox.width,
        scrollBoxAbsoluteEdgeBegin: window.scrollX,
        scrollBoxSize: document.documentElement.clientWidth,
    });
    window.scroll({ top, left, behavior });
}

export function scrollElementIntoView(element, behavior, block, inline) {
    // 1. “If the Document associated with element is not same origin with the Document associated with the element or viewport associated with box, terminate these steps.”
    // TODO (if this makes sense here at all?)

    // 2. “Let element bounding border box be the box that the return value of invoking getBoundingClientRect() on element represents.”
    const elementBoundingBorderBox = element.getBoundingClientRect();

    // 3. “Perform scroll a DOMRect into view given element bounding border box, options and element.”
    // XXX There is no “options” defined; presumably that should be “behavior, block, inline”.
    scrollDomRectIntoView(elementBoundingBorderBox, behavior, block, inline, element);
}

export function scrollRangeIntoView(range, behavior, block, inline, containingElement) {
    // 1. “Let bounding rect be the DOMRect that is the return value of invoking getBoundingClientRect() on range.”
    const boundingRect = range.getBoundingClientRect();

    // 2. “Perform scroll a DOMRect into view given bounding rect, behavior, block, inline, and containingElement.”
    scrollDomRectIntoView(boundingRect, behavior, block, inline, containingElement);
}

export function processFragmentDirective(fragmentDirectiveInput, document) {
    // 1. “If fragment directive input is not a valid fragment directive, then return an empty list.”
    if (!isValidFragmentDirective(fragmentDirectiveInput)) {
        return [];
    }

    // 2. “Let directives be a list of ASCII strings that is the result of strictly splitting the string fragment directive input on "&".”
    const directives = fragmentDirectiveInput.split('&');

    // 3. “Let ranges be a list of ranges, initially empty.”
    const ranges = [];

    // 4. “For each ASCII string directive of directives:”
    for (const directive of directives) {
        // 1. “If directive does not match the production TextDirective, then continue.”
        if (!isTextFragmentDirective(directive))
            continue;

        // 2. “Let parsedValues be the result of running the parse a text directive steps on directive.”
        const parsedValues = parseTextDirective(directive);

        // 3. “If parsedValues is null then continue.”
        if (parsedValues === null)
            continue;

        // 4. “If the result of running find a range from a text directive given parsedValues and document is non-null, then append it to ranges.”
        const range = findRangeFromTextDirective(parsedValues, document);
        if (range !== null)
            ranges.push(range);
    }

    // 5. “Return ranges.”
    return ranges;
}

export function findRangeFromTextDirective(parsedValues, document) {
    // 1. “Let searchRange be a range with start (document, 0) and end (document, document’s length)”
    const searchRange = document.createRange();
    searchRange.setStart(document, 0);
    searchRange.setEnd(document, document.childNodes.length);

    // 2. “While searchRange is not collapsed:”
    while (!searchRange.collapsed) {
        // 1. “Let potentialMatch be null.”
        let potentialMatch = null;
        // 2. “If parsedValues’s prefix is not null:”
        if (parsedValues.prefix !== null) {
            // 1. “Let prefixMatch be the the result of running the find a string in range steps with query parsedValues’s prefix, searchRange searchRange, wordStartBounded true and wordEndBounded false.”
            const prefixMatch = findStringInRange(parsedValues.prefix, searchRange, true, false);

            // 2. “If prefixMatch is null, return null.”
            if (prefixMatch === null)
                return null;

            // 3. “Set searchRange’s start to the first boundary point after prefixMatch’s start”
            searchRange.setStart(...firstBoundaryPointAfter(getStart(prefixMatch)));

            // 4. “Let matchRange be a range whose start is prefixMatch’s end and end is searchRange’s end.”
            const matchRange = document.createRange();
            matchRange.setStart(...getEnd(prefixMatch));
            matchRange.setEnd(...getEnd(searchRange));

            // 5. “Advance matchRange’s start to the next non-whitespace position.”
            advanceRangeStartToNextNonWhitespacePosition(matchRange);

            // 6. “If matchRange is collapsed return null.”
            if (matchRange.collapsed)
                return null;

            // 7. “Assert: matchRange’s start node is a Text node.”
            // assert(matchRange.startContainer.nodeType === Node.TEXT_NODE);

            // 8. “Let mustEndAtWordBoundary be true if parsedValues’s textEnd is non-null or parsedValues’s suffix is null, false otherwise.”
            const mustEndAtWordBoundary = (parsedValues.textEnd !== null || parsedValues.suffix === null);

            // 9. “Set potentialMatch to the result of running the find a string in range steps with query parsedValues’s textStart, searchRange matchRange, wordStartBounded false, and wordEndBounded mustEndAtWordBoundary.”
            potentialMatch = findStringInRange(parsedValues.textStart, matchRange, false, mustEndAtWordBoundary);

            // 10. “If potentialMatch is null, return null.”
            if (potentialMatch === null)
                return null;

            // 11. “If potentialMatch’s start is not matchRange’s start, then continue.”
            if (!samePoint(getStart(potentialMatch), getStart(matchRange)))
                continue;
        }
        // 3. “Otherwise:”
        else {
            // 1. “Let mustEndAtWordBoundary be true if parsedValues’s textEnd is non-null or parsedValues’s suffix is null, false otherwise.”
            const mustEndAtWordBoundary = (parsedValues.textEnd !== null || parsedValues.suffix === null);

            // 2. “Set potentialMatch to the result of running the find a string in range steps with query parsedValues’s textStart, searchRange searchRange, wordStartBounded true, and wordEndBounded mustEndAtWordBoundary.”
            potentialMatch = findStringInRange(parsedValues.textStart, searchRange, true, mustEndAtWordBoundary);

            // 3. “If potentialMatch is null, return null.”
            if (potentialMatch === null)
                return null;

            // 4. “Set searchRange’s start to the first boundary point after potentialMatch’s start”
            searchRange.setStart(...firstBoundaryPointAfter(getStart(potentialMatch)));
        }

        // 4. “If parsedValues’s textEnd item is non-null, then:”
        if (parsedValues.textEnd !== null) {
            // 1. “Let textEndRange be a range whose start is potentialMatch’s end and whose end is searchRange’s end.”
            const textEndRange = document.createRange();
            textEndRange.setStart(...getEnd(potentialMatch));
            textEndRange.setEnd(...getEnd(searchRange));

            // 2. “Let mustEndAtWordBoundary be true if parsedValues’s suffix is null, false otherwise.”
            const mustEndAtWordBoundary = parsedValues.suffix === null;

            // 3. “Let textEndMatch be the result of running the find a string in range steps with query parsedValues’s textEnd, searchRange textEndRange, wordStartBounded true, and wordEndBounded mustEndAtWordBoundary.”
            const textEndMatch = findStringInRange(parsedValues.textEnd, textEndRange, true, mustEndAtWordBoundary);

            // 4. “If textEndMatch is null then return null.”
            if (textEndMatch === null)
                return null;

            // 5. “Set potentialMatch’s end to textEndMatch’s end.”
            potentialMatch.setEnd(...getEnd(textEndMatch));
        }

        // 5. “Assert: potentialMatch is non-null, not collapsed and represents a range exactly containing an instance of matching text.” XXX the last assertion sounds rather vague.
        // assert(
        //     potentialMatch !== null
        //     && !potentialMatch.collapsed
        //     && new RegExp('^' + escapeRegExp(textStart) + '.*' + escapeRegExp(textEnd) + '$').test(potentialMatch.toString())  // …or something similar?
        // );

        // 6. “If parsedValues’s suffix is null, return potentialMatch.”
        if (parsedValues.suffix === null)
            return potentialMatch;

        // 7. “Let suffixRange be a range with start equal to potentialMatch’s end and end equal to searchRange’s end.”
        const suffixRange = document.createRange();
        suffixRange.setStart(...getEnd(potentialMatch));
        suffixRange.setEnd(...getEnd(searchRange));

        // 8. “Advance suffixRange’s start to the next non-whitespace position.”
        advanceRangeStartToNextNonWhitespacePosition(suffixRange);

        // 9. “Let suffixMatch be result of running the find a string in range steps with query parsedValues’s suffix, searchRange suffixRange, wordStartBounded false, and wordEndBounded true.”
        const suffixMatch = findStringInRange(parsedValues.suffix, suffixRange, false, true);

        // 10. “If suffixMatch is null then return null.”
        if (suffixMatch === null)
            return null;

        // 11. “If suffixMatch’s start is suffixRange’s start, return potentialMatch.”
        if (samePoint(getStart(suffixMatch), getStart(suffixRange)))
            return potentialMatch;
    }

    // 3. “Return null”
    return null;
}

function advanceRangeStartToNextNonWhitespacePosition(range) {
    // 1. “While range is not collapsed:”
    while (!range.collapsed) {
        // 1. “Let node be range’s start node.”
        const node = range.startContainer;

        // 2. “Let offset be range’s start offset.”
        const offset = range.startOffset;

        // 3. “If node is part of a non-searchable subtree then:”
        if (partOfNonSearchableSubtree(node)) {
            // 1. “Set range’s start node to the next node, in shadow-including tree order, that isn’t a shadow-including descendant of node, and set its start offset to 0.”
            range.setStart(
                nextNodeInShadowIncludingTreeOrderThatIsNotAShadowIncludingDescendantOf(node),
                0
            );

            // 2. “Continue.”
            continue;
        }

        // 4. “If node is not a visible text node:”
        if (!isVisibleTextNode(node)) {
            // 1. “Set range’s start node to the next node, in shadow-including tree order, and set its start offset to 0.”
            range.setStart(
                nextNodeInShadowIncludingTreeOrder(node),
                0
            );
            // 2. “Continue.”
            continue;
        }

        // 5. “If the substring data of node at offset offset and count 6 is equal to the string "&nbsp;" then:” XXX Why only "&nbsp;", and not e.g. "&thinspace;" or others? Is there no existing spec for whitespace that can be used here?
        if (substringData(node, offset, 6) === '&nbsp;') {
            // 1. “Add 6 to range’s start offset.”
            range.setStart(range.startContainer, range.startOffset + 6);
        }

        // 6. “Otherwise, if the substring data of node at offset offset and count 5 is equal to the string "&nbsp" then:”
        else if (substringData(node, offset, 5) === '&nbsp') {
            // 1. “Add 5 to range’s start offset.”
            range.setStart(range.startContainer, range.startOffset + 5);
        }

        // 7. “Otherwise”
        else {
            // 1. “Let cp be the code point at the offset index in node’s data.”
            const cp = node.data.codePointAt(offset);

            // 2. “If cp does not have the White_Space property set, return.”
            if (!hasWhiteSpaceProperty(cp)) return;

            // 3. “Add 1 to range’s start offset.”
            range.setStart(range.startContainer, range.startOffset + 1);
        }

        // 8. “If range’s start offset is equal to node’s length, set range’s start node to the next node in shadow-including tree order, and set its start offset to 0.”
        if (range.startOffset === nodeLength(node)) {
            range.setStart(
                nextNodeInShadowIncludingTreeOrder(node),
                0
            );
        }
    }
}

function findStringInRange(query, searchRange, wordStartBounded, wordEndBounded) {
    // 1. “While searchRange is not collapsed:”
    while (!searchRange.collapsed) {
        // 1. “Let curNode be searchRange’s start node.”
        let curNode = searchRange.startContainer;

        // 2. “If curNode is part of a non-searchable subtree:”
        if (partOfNonSearchableSubtree(curNode)) {
            // 1. “Set searchRange’s start node to the next node, in shadow-including tree order, that isn’t a shadow-including descendant of curNode”
            searchRange.setStart(
                nextNodeInShadowIncludingTreeOrderThatIsNotAShadowIncludingDescendantOf(curNode),
                0
            );

            // 2. “Continue.”
            continue;
        }

        // 3. “If curNode is not a visible text node:”
        if (!isVisibleTextNode(curNode)) {
            // 1. “Set searchRange’s start node to the next node, in shadow-including tree order, that is not a doctype, and set its start offset to 0.”
            curNode = nextNodeInShadowIncludingTreeOrder(curNode);
            while (curNode && curNode.nodeType === Node.DOCUMENT_TYPE_NODE)
                curNode = nextNodeInShadowIncludingTreeOrder(curNode);
            searchRange.setStart(curNode, 0);

            // 2. “Continue.”
            continue;
        }

        // 4. “Let blockAncestor be the nearest block ancestor of curNode.”
        const blockAncestor = nearestBlockAncestorOf(curNode);

        // 5. “Let textNodeList be a list of Text nodes, initially empty.”
        const textNodeList = [];

        // 6. “While curNode is a shadow-including descendant of blockAncestor and the position of the boundary point (curNode, 0) is not after searchRange’s end:”
        while (
            curNode && isShadowIncludingDescendant(curNode, blockAncestor) &&
            searchRange.comparePoint(curNode, 0) !== 1
        ) {
            // 1. “If curNode has block-level display then break.”
            if (hasBlockLevelDisplay(curNode)) {
                break;
            }

            // 2. “If curNode is search invisible:”
            if (isSearchInvisible(curNode)) {
                // 1. “Set curNode to the next node in shadow-including tree order that isn’t a shadow-including descendant of curNode.”
                curNode = nextNodeInShadowIncludingTreeOrderThatIsNotAShadowIncludingDescendantOf(curNode);

                // 2. “Continue.”
                continue;
            }

            // 3. “If curNode is a visible text node then append it to textNodeList.”
            if (isVisibleTextNode(curNode)) {
                textNodeList.push(curNode);
            }

            // 4. “Set curNode to the next node in shadow-including tree order.”
            curNode = nextNodeInShadowIncludingTreeOrder(curNode);
        }

        // 7. “Run the find a range from a node list steps given query, searchRange, textNodeList, wordStartBounded and wordEndBounded as input. If the resulting range is not null, then return it.”
        const resultingRange = findARangeFromANodeList(query, searchRange, textNodeList, wordStartBounded, wordEndBounded);
        if (resultingRange !== null) {
            return resultingRange;
        }

        // 8. “If curNode is null, then break.”
        if (curNode === null)
            break;

        // 9. “Assert: curNode follows searchRange’s start node.”
        // assert(searchRange.startContainer.compareDocumentPosition(curNode) & Node.DOCUMENT_POSITION_FOLLOWING);

        // 10. “Set searchRange’s start to the boundary point (curNode, 0).”
        searchRange.setStart(curNode, 0);
    }

    // 2. “Return null.”
    return null;
}


function isSearchInvisible(node) {
    // “…if it is an element in the HTML namespace and meets any of the following conditions:”
    if (isElement(node) && node.namespaceURI === htmlNamespace) {

        // 1. “The computed value of its display property is none.”
        if (getComputedStyle(node).display === 'none')
            return true;

        // 2. “If the node serializes as void.”
        if (serializesAsVoid(node))
            return true;

        // 3. “Is any of the following types: HTMLIFrameElement, HTMLImageElement, HTMLMeterElement, HTMLObjectElement, HTMLProgressElement, HTMLStyleElement, HTMLScriptElement, HTMLVideoElement, HTMLAudioElement”
        if (['iframe', 'image', 'meter', 'object', 'progress', 'style', 'script', 'video', 'audio'].includes(node.localName))
            return true;

        // 4. “Is a select element whose multiple content attribute is absent.”
        if (node.localName === 'select' && !node.hasAttribute('multiple'))
            return true;

        // 5. avoid the following classnames
        if (['frame__search__snippets', 'gallery', 'button', 'toc', 'breadcrumbs__wrap__item', 'authors-content', 'topic-intro-section'].some(className => node.classList.contains(className)))
            return true;

    }
    return false;
}



function partOfNonSearchableSubtree(node) {
    let curNode = node;
    while (curNode) {
        if (isSearchInvisible(curNode))
            return true;
        curNode = shadowIncludingParent(curNode);
    }
    return false;
}

function isVisibleTextNode(node) {
    return (
        node.nodeType === Node.TEXT_NODE &&
        node.parentElement !== null &&
        getComputedStyle(node.parentElement).visibility === 'visible' &&
        isBeingRendered(node.parentElement)
    );
}

function hasBlockLevelDisplay(node) {
    return (
        isElement(node) &&
        ['block', 'table', 'flow-root', 'grid', 'flex', 'list-item'].includes(getComputedStyle(node).display)
    );
}

function nearestBlockAncestorOf(node) {
    let curNode = node;
    while (curNode !== null) {
        if (curNode.nodeType !== Node.TEXT_NODE && hasBlockLevelDisplay(curNode))
            return curNode;
        else
            curNode = curNode.parentNode;
    }
    return (node.ownerDocument || node).documentElement;
}


function findARangeFromANodeList(queryString, searchRange, nodes, wordStartBounded, wordEndBounded) {
    // 1. “Let searchBuffer be the concatenation of the data of each item in nodes.”
    const searchBuffer = nodes.map(node => node.data).join('');

    // 2. “Let searchStart be 0.”
    let searchStart = 0;

    // 3. “If the first item in nodes is searchRange’s start node then set searchStart to searchRange’s start offset.”
    if (nodes[0] === searchRange.startContainer)
        searchStart = searchRange.startOffset;

    // 4. “Let start and end be boundary points, initially null.”
    let start = null;
    let end = null;

    // 5. “Let matchIndex be null.”
    let matchIndex = null;

    // 6. “While matchIndex is null”
    while (matchIndex === null) {
        // 1. “Set matchIndex to the index of the first instance of queryString in searchBuffer, starting at searchStart. The string search must be performed using a base character comparison, or the primary level, as defined in [UTS10].”
        matchIndex = searchBuffer.toLowerCase().indexOf(queryString.toLowerCase(), searchStart);

        // 2. “If matchIndex is null, return null.”
        if (matchIndex === -1)
            return null;

        // 3. “Let endIx be matchIndex + queryString’s length.”
        const endIx = matchIndex + queryString.length;

        // 4. “Set start to the boundary point result of get boundary point at index matchIndex run over nodes with isEnd false.”
        start = getBoundaryPointAtIndex(matchIndex, nodes, false);

        // 5. “Set end to the boundary point result of get boundary point at index endIx run over nodes with isEnd true.”
        end = getBoundaryPointAtIndex(endIx, nodes, true);

        // 6. “If wordStartBounded is true and matchIndex is not at a word boundary in searchBuffer, given the language from start’s node as the locale; or wordEndBounded is true and matchIndex + queryString’s length is not at a word boundary in searchBuffer, given the language from end’s node as the locale:”
        if (
            (wordStartBounded && !isAtWordBoundary(matchIndex, searchBuffer)) ||
            (wordEndBounded && !isAtWordBoundary(matchIndex + queryString.length, searchBuffer))
        ) {
            // 1. “Set searchStart to matchIndex + 1.”
            searchStart = matchIndex + 1;

            // 2. “Set matchIndex to null.”
            matchIndex = null;
        }
    }

    // 7. “Let endInset be 0.”
    let endInset = 0;

    // 8. “If the last item in nodes is searchRange’s end node then set endInset to (searchRange’s end node's length − searchRange’s end offset)”
    if (nodes[nodes.length - 1] === searchRange.endContainer)
        endInset = searchRange.endContainer.length - searchRange.endOffset;

    // 9. “If matchIndex + queryString’s length is greater than searchBuffer’s length − endInset return null.”
    if (matchIndex + queryString.length > searchBuffer.length - endInset)
        return null;

    // 10. “Assert: start and end are non-null, valid boundary points in searchRange.”
    start = start;
    end = end;

    // 11. “Return a range with start start and end end.”
    const result = document.createRange();
    result.setStart(...start);
    result.setEnd(...end);
    return result;
}


/**
 * Gets the boundary point at a specified index within a list of nodes.
 *
 * @param {number} index - The index at which to find the boundary point.
 * @param {Array} nodes - An array of nodes to search through.
 * @param {boolean} isEnd - A flag indicating whether to adjust for the end boundary.
 * @returns {Array|null} An array containing the node and the offset within the node, or null if the index is out of bounds.
 */
function getBoundaryPointAtIndex(index, nodes, isEnd) {
    let counted = 0;
    for (const curNode of nodes) {
        let nodeEnd = counted + curNode.length;
        if (isEnd) nodeEnd += 1;
        if (nodeEnd > index) {
            return [curNode, index - counted];
        }
        counted += curNode.length;
    }
    return null;
}


/**
 * Checks if the given position in the text is at a word boundary.
 *
 * A position is considered at a word boundary if:
 * - The character at the position is not part of a word (i.e., not alphanumeric).
 * - The position is at the start or end of the text.
 *
 * @param {number} position - The position in the text to check.
 * @param {string} text - The text in which to check the position.
 * @param {string} locale - The locale to use for word boundary rules (currently unused).
 * @returns {boolean} - Returns true if the position is at a word boundary, false otherwise.
 */
function isAtWordBoundary(position, text, locale) {
    if (text.charAt(position) && text.substring(position - 1, position + 1).match(/^[\w\d]{2,2}$/) === null)
        return true;

    if (text.length > 0 && (position === 0 || position === text.length))
        return true;

    return false;
}

/**
 * Retrieves the start container and offset of a given range.
 *
 * @param {Range} range - The range object from which to get the start container and offset.
 * @returns {Array} An array containing the start container and the start offset.
 */
function getStart(range) {
    return [range.startContainer, range.startOffset];
}


/**
 * Retrieves the end container and offset of a given range.
 *
 * @param {Range} range - The range object from which to get the end container and offset.
 * @returns {[Node, number]} An array containing the end container (Node) and the end offset (number).
 */
function getEnd(range) {
    return [range.endContainer, range.endOffset];
}


/**
 * Compares two points to determine if they are the same.
 *
 * @param {number[]} point1 - The first point, represented as an array with two elements [x, y].
 * @param {number[]} point2 - The second point, represented as an array with two elements [x, y].
 * @returns {boolean} - Returns true if both points have the same coordinates, otherwise false.
 */
function samePoint(point1, point2) {
    return point1[0] === point2[0] && point1[1] === point2[1];
}


/**
 * Returns the next node in the shadow-including tree order that is not a shadow-including descendant of the given node.
 *
 * @param {Node} node - The reference node.
 * @returns {Node|null} The next node in the shadow-including tree order that is not a shadow-including descendant of the given node, or null if no such node exists.
 */
function nextNodeInShadowIncludingTreeOrderThatIsNotAShadowIncludingDescendantOf(node) {
    let curNode = nextNodeInShadowIncludingTreeOrder(node);
    while (curNode && isShadowIncludingDescendant(curNode, node)) {
        curNode = nextNodeInShadowIncludingTreeOrder(curNode);
    }
    return curNode;
}



/**
 * Checks if the given Unicode code point is a whitespace character.
 *
 * @param {number} codePoint - The Unicode code point to check.
 * @returns {boolean} True if the code point is a whitespace character, false otherwise.
 */
function hasWhiteSpaceProperty(codePoint) {
    const whitespaceCodePoints = [
        0x0009, 0x000A, 0x000B, 0x000C, 0x000D,
        0x0085, 0x2028, 0x2029, 0x0020, 0x3000,
        0x1680, 0x2000, 0x2001, 0x2002, 0x2003,
        0x2004, 0x2005, 0x2006, 0x2008, 0x2009,
        0x200A, 0x205F, 0x00A0, 0x2007, 0x202F,
    ];

    return whitespaceCodePoints.includes(codePoint);
}

/**
 * Returns the first boundary point after the given boundary point.
 *
 * @param {Array} boundaryPoint - An array containing a node and an offset.
 * @param {Node} boundaryPoint[0] - The node in the boundary point.
 * @param {number} boundaryPoint[1] - The offset in the boundary point.
 * @returns {Array|null} An array containing the next node and offset, or null if there is no next node.
 */
function firstBoundaryPointAfter(boundaryPoint) {
    const [node, offset] = boundaryPoint;
    if (offset < nodeLength(node)) {
        return [node, offset + 1];
    } else {
        const next = nextNode(node);
        if (next !== null)
            return [next, 0];
        else
            return null;
    }
}